import { useCallback } from 'react';
import {
  useCreateSettingValue,
  useUpdateSettingValue,
  useDeleteSettingValue,
  useFindManySettingValue,
} from '@erikdakoda/database/hooks/setting-value';
import { useFindManySettingTemplate } from '@erikdakoda/database/hooks/setting-template';
import {
  encodeSettingValue,
  getSettingType,
  decodeSettingValue,
} from '@erikdakoda/user/server/settings';
import { doFetch } from '../shared/doFetch';
import { useCurrentUser } from './useCurrentUser';
import {
  SettingTemplateForReadSelect,
  SettingValueForReadSelect,
  type SettingValueForRead,
} from '@erikdakoda/user/types';
import { getCurrentAppId } from '@erikdakoda/utils/getCurrentAppId';

type SettingValueInput = string | number | boolean | object | Date;

type WriteSettingOptions = {
  group?: string | null;
  public?: boolean;
};

type ReadSettingOptions = {
  group?: string | null;
  allowAnonymous?: boolean;
};

type DeleteSettingOptions = {
  group?: string | null;
};

/**
 * Matches a setting row by key/app and optional group.
 * When `group` is omitted, matches any group for that key (legacy callers).
 */
export function matchesSettingKey(
  setting: { key: string; app: string; group: string | null },
  key: string,
  appId: string,
  group?: string | null,
): boolean {
  if (setting.key !== key || setting.app !== appId) {
    return false;
  }
  if (group === undefined) {
    return true;
  }
  return setting.group === group;
}

/**
 * Returns SettingTemplate spots visible for a user role.
 * @param role Current user's role.
 * @returns Spot names the user may read in Preferences / useSettings.
 */
export function getAllowedSettingSpots(role: string | undefined): string[] {
  if (role === 'ADMIN') {
    return ['user', 'team', 'admin'];
  }
  if (role === 'TEAM') {
    return ['user', 'team'];
  }
  return ['user'];
}

/**
 * Hook for managing user settings on the client side.
 * Resolve preference overrides with `{ group }` so keys shared across groups
 * (e.g. convo vs agent-convo) do not collide.
 */
export function useSettings() {
  const { user } = useCurrentUser();
  const appId = getCurrentAppId();
  const allowedSpots = getAllowedSettingSpots(user?.role);
  const createMutation = useCreateSettingValue();
  const updateMutation = useUpdateSettingValue();
  const deleteMutation = useDeleteSettingValue();

  const { data: allSettings = [], isLoading: isSettingsLoading } = useFindManySettingValue(
    {
      where: { ownerId: user?.id, app: appId },
      select: SettingValueForReadSelect,
    },
    {
      enabled: Boolean(user?.id),
    },
  );

  const { data: templates = [], isLoading: isTemplatesLoading } = useFindManySettingTemplate(
    {
      where: {
        spot: { in: allowedSpots },
        OR: [{ apps: { isEmpty: true } }, { apps: { has: appId } }],
      },
      select: SettingTemplateForReadSelect,
    },
    {
      enabled: Boolean(user?.id),
    },
  );

  /**
   * Write a setting value for the current user.
   * Preference writes should pass `group` matching the SettingTemplate.
   */
  const writeSetting = useCallback(
    async (key: string, value: SettingValueInput, options?: WriteSettingOptions): Promise<void> => {
      if (!user?.id) {
        throw new Error('User must be authenticated to write settings');
      }

      const type = getSettingType(value);

      if (!type) {
        throw new Error('Invalid value type');
      }

      const valueString = encodeSettingValue(value);
      const group = options?.group ?? null;

      const existingSetting = allSettings.find((setting) =>
        matchesSettingKey(setting, key, appId, group),
      );

      if (existingSetting) {
        await updateMutation.mutateAsync({
          where: { id: existingSetting.id },
          data: {
            type,
            value: valueString,
            ...(group != null ? { group } : { group: null }),
            ...(options?.public !== undefined ? { public: options.public } : {}),
          },
        });
      } else {
        await createMutation.mutateAsync({
          data: {
            key,
            type,
            value: valueString,
            app: appId,
            ...(group != null ? { group } : {}),
            public: options?.public ?? false,
            ownerId: user.id,
          },
        });
      }
    },
    [user?.id, createMutation, updateMutation, allSettings, appId],
  );

  /**
   * Read a setting value for the current user.
   * Returns the typed value, template default (group-matched), or null if not found.
   */
  const readSetting = useCallback(
    <T = SettingValueInput>(key: string, options?: ReadSettingOptions): T | null => {
      if (!user?.id && !options?.allowAnonymous) {
        throw new Error('User must be authenticated to read settings');
      }

      const group = options?.group;
      const setting = allSettings.find((row) => matchesSettingKey(row, key, appId, group));

      if (setting) {
        return decodeSettingValue<T>(setting);
      }

      const template =
        group === undefined
          ? templates.find((row) => row.key === key)
          : templates.find((row) => row.key === key && row.group === group);
      if (!template) {
        return null;
      }

      return decodeSettingValue<T>({
        id: template.id,
        ownerId: user?.id ?? '',
        key: template.key,
        value: template.default,
        type: template.type,
        app: appId,
        group: template.group,
        public: template.public,
      });
    },
    [user?.id, allSettings, templates, appId],
  );

  /**
   * Read a public setting value on-demand (fetches when called)
   * Returns the typed value or null if not found
   */
  const readPublicSetting = useCallback(
    async <T = SettingValueInput>(key: string): Promise<T | null> => {
      const result = await doFetch<{ data: SettingValueForRead | null }>({
        url: '/api/model/settingValue/findFirst',
        params: {
          q: JSON.stringify({
            where: { key, public: true, app: getCurrentAppId() },
            select: SettingValueForReadSelect,
          }),
        },
      });

      const setting = result?.data;

      if (!setting) return null;

      const value = decodeSettingValue<T>(setting);
      return value as T;
    },
    [],
  );

  /**
   * Delete a setting for the current user.
   * Pass `group` when deleting a preference override for a shared key.
   * Returns false when no row matches; a failed delete rejects, so the caller shows the error.
   */
  const deleteSetting = useCallback(
    async (key: string, options?: DeleteSettingOptions): Promise<boolean> => {
      if (!user?.id) {
        throw new Error('User must be authenticated to delete settings');
      }

      const setting = allSettings.find((row) => matchesSettingKey(row, key, appId, options?.group));

      if (!setting) return false;

      await deleteMutation.mutateAsync({
        where: { id: setting.id },
      });

      return true;
    },
    [user?.id, deleteMutation, allSettings, appId],
  );

  return {
    writeSetting,
    readSetting,
    readPublicSetting,
    deleteSetting,
    templates,
    allSettings,
    appId,
    isTemplatesLoading,
    isDisplaySettingsLoading: Boolean(user?.id) && (isSettingsLoading || isTemplatesLoading),
    isLoading: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    error: createMutation.error || updateMutation.error || deleteMutation.error,
  };
}
